import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Accordion - Componente de seção expansível padronizado
 * 
 * CONTEXTO DE NEGÓCIO:
 * Unifica o padrão visual e de interação para todas as seções expansíveis da aplicação,
 * reduzindo poluição visual e evitando confusão com banners de aviso.
 * 
 * PADRÕES DE DESIGN:
 * - Header neutro (bg-white/gray, borda inferior)
 * - Ícone de chevron indicando estado expandido/recolhido
 * - Acessibilidade com role/button e aria-expanded
 * 
 * USO TÍPICO:
 * <Accordion titulo="Prognóstico e Cenários" descricao="Probabilidades de êxito" defaultOpen>
 *   <Conteudo />
 * </Accordion>
 */
export interface AccordionProps {
  /** Título principal do accordion */
  titulo: string;
  /** Descrição curta opcional apresentada abaixo do título */
  descricao?: string;
  /** Abre inicialmente o conteúdo (default: true) */
  defaultOpen?: boolean;
  /** Adorno opcional à esquerda do título (ex.: badge com número) */
  prefixo?: React.ReactNode;
  /** Conteúdo da seção expansível */
  children: React.ReactNode;
  /** Classe extra aplicada ao container externo */
  className?: string;
}

export function Accordion({
  titulo,
  descricao,
  defaultOpen = true,
  prefixo,
  children,
  className,
}: AccordionProps): JSX.Element {
  const [aberto, setAberto] = useState<boolean>(defaultOpen);

  return (
    <section className={`overflow-hidden rounded-xl border border-gray-200 bg-white shadow-md ${className ?? ''}`}>
      {/* Header neutro e consistente */}
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className="flex w-full items-center gap-3 border-b border-gray-200 bg-white px-5 py-4 transition-colors hover:bg-gray-50"
        role="button"
        aria-expanded={aberto}
      >
        {/* Prefixo opcional (ex.: badge numerado) */}
        {prefixo && (
          <span className="inline-flex size-8 items-center justify-center rounded-full border border-gray-200 bg-gray-100 text-sm font-bold text-gray-700">
            {prefixo}
          </span>
        )}

        <div className="min-w-0 flex-1 text-left">
          <h3 className="truncate text-base font-semibold text-gray-900 md:text-lg">{titulo}</h3>
          {descricao && (
            <p className="mt-0.5 truncate text-sm text-gray-600">{descricao}</p>
          )}
        </div>

        {aberto ? (
          <ChevronUp className="size-5 shrink-0 text-gray-500" />
        ) : (
          <ChevronDown className="size-5 shrink-0 text-gray-500" />
        )}
      </button>

      {/* Corpo do accordion */}
      {aberto && (
        <div className="p-5">
          {children}
        </div>
      )}
    </section>
  );
}

export default Accordion;
